import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, CheckCircle2, RotateCcw, Upload } from "lucide-react";
import { toast } from "sonner";
import { compressImageAsync } from "@/lib/imageAsync";

/**
 * Dialog obrigatório para capturar a foto de evidência ANTES de finalizar.
 * - Somente câmera (accept="image/*" + capture="environment") → sem galeria em mobile.
 * - Faz upload imediato para /api/minhas-entregas/{eid}/evidencia.
 * - Ao concluir, chama onUploaded() para o próximo passo (código de 4 dígitos / motivo).
 *
 * Props:
 *   open, onOpenChange, entregaId, statusLabel, onUploaded
 */
export function EvidenceCaptureDialog({ open, onOpenChange, entregaId, statusLabel, onUploaded }) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [compressing, setCompressing] = useState(false);
  // Estado "sucesso" — mostra CheckCircle verde "Enviado!" no botão Enviar por
  // 1s antes de fechar o dialog. Melhora feedback (UX fev/2026).
  const [uploadedOk, setUploadedOk] = useState(false);
  // Ref pra evitar setState após unmount (previne "Can't perform a React state update
  // on an unmounted component" e crashes de DOM em cascata do Radix Portal)
  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  // Revoga ObjectURL do preview anterior — evita memory leak (bug fev/2026)
  const revokeIfSet = (url) => { if (url) try { URL.revokeObjectURL(url); } catch { /* no-op */ } };
  const reset = () => {
    revokeIfSet(preview);
    setPreview(null); setFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };
  // Cleanup ao desmontar (dialog fechado + component unmount)
  useEffect(() => () => revokeIfSet(preview), [preview]);

  const handlePick = async (e) => {
    const raw = e.target.files?.[0];
    if (!raw) return;
    if (!raw.type.startsWith("image/")) return toast.error("Apenas fotos");
    if (raw.size > 20 * 1024 * 1024) return toast.error("Foto maior que 20MB — tire novamente");
    // Compressão assíncrona (createImageBitmap + OffscreenCanvas quando disponível).
    // Não bloqueia a UI mesmo com fotos grandes de smartphone (3-8MB → ~500KB).
    setCompressing(true);
    const compressed = await compressImageAsync(raw);
    // Guarda: se o Dialog foi fechado durante a compressão (usuário cancelou),
    // não tenta atualizar estado desmontado — previne crash de DOM do React.
    if (!mountedRef.current) return;
    setCompressing(false);
    revokeIfSet(preview);
    setFile(compressed);
    setPreview(URL.createObjectURL(compressed));
    if (compressed !== raw && compressed.size < raw.size) {
      const savedPct = Math.round((1 - compressed.size / raw.size) * 100);
      if (savedPct >= 20) toast.success(`Foto otimizada · ${savedPct}% menor`);
    }
  };

  const submit = async () => {
    if (!file) return toast.error("Tire uma foto primeiro");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file, file.name || "evidencia.jpg");
    try {
      await api.post(`/minhas-entregas/${entregaId}/evidencia`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Foto enviada");
      // Green check "Enviado!" por 1s antes de fechar (UX fev/2026)
      if (mountedRef.current) { setUploading(false); setUploadedOk(true); }
      await new Promise((r) => setTimeout(r, 1000));
      if (!mountedRef.current) return;
      // flushSync força o commit síncrono do fechamento — evita que o próximo
      // Dialog (MobileStatusPrompt) monte antes deste terminar de desmontar.
      // Evita crash React 18: "Failed to execute insertBefore on Node".
      flushSync(() => {
        setUploadedOk(false);
        reset();
        onOpenChange(false);
      });
      // Aguarda 2 rAFs + delay para animação do Radix Dialog (200ms) completar
      // ANTES de abrir o próximo diálogo. Sem esse delay dois portais colidem.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (mountedRef.current) onUploaded?.();
          }, 280);
        });
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar foto");
      if (mountedRef.current) setUploading(false);
    }
  };

  // Impede fechar o dialog durante o upload OU a animação de 1s do "Enviado!" —
  // se o usuário tentar clicar fora / ESC, o Radix cancelaria o portal no meio da
  // transição, causando crash de DOM. Também sincroniza com o botão desabilitado.
  const dialogClose = (o) => {
    if (uploading || uploadedOk) return;  // trava até o fluxo terminar
    if (!o) reset();
    onOpenChange(o);
  };

  return (
    <Dialog open={open} onOpenChange={dialogClose}>
      <DialogContent className="max-w-md" data-testid="dialog-evidencia">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-orange-600" />
            Evidência fotográfica
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm">
            <div className="font-bold text-orange-800">Antes de {statusLabel?.toLowerCase() || "finalizar"}:</div>
            <div className="text-orange-900">Tire uma foto <b>agora</b> mostrando o pacote no local (ou com o cliente). A foto será enviada ao painel administrativo junto com o GPS.</div>
          </div>

          {preview ? (
            <div className="rounded-lg overflow-hidden border-2 border-orange-300">
              <img src={preview} alt="Prévia da evidência" className="w-full h-64 object-cover" data-testid="evidencia-preview" />
            </div>
          ) : (
            <div className="rounded-lg border-2 border-dashed border-orange-300 h-64 flex flex-col items-center justify-center bg-orange-50/40 text-orange-700">
              <Camera className="w-12 h-12 mb-2" />
              <div className="text-sm">Nenhuma foto ainda</div>
            </div>
          )}

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handlePick}
            className="hidden"
            data-testid="evidencia-file-input"
          />

          <div className="grid grid-cols-2 gap-2">
            <Button
              variant={preview ? "outline" : "default"}
              onClick={() => fileRef.current?.click()}
              disabled={uploading || compressing}
              className={preview ? "" : "bg-orange-600 hover:bg-orange-700"}
              data-testid="evidencia-btn-capturar"
            >
              <Camera className="w-4 h-4 mr-2" />
              {compressing ? "Otimizando…" : (preview ? "Tirar de novo" : "Abrir câmera")}
            </Button>
            <Button
              onClick={submit}
              disabled={!preview || uploading || compressing || uploadedOk}
              className={`${uploadedOk ? "bg-green-600 hover:bg-green-600 ring-2 ring-green-300" : "bg-green-600 hover:bg-green-700"}`}
              data-testid="evidencia-btn-enviar"
            >
              {uploadedOk ? (
                <><CheckCircle2 className="w-4 h-4 mr-2" />Enviado!</>
              ) : uploading ? (
                <><Upload className="w-4 h-4 mr-2 animate-pulse" />Enviando...</>
              ) : (
                <><CheckCircle2 className="w-4 h-4 mr-2" />Enviar foto</>
              )}
            </Button>
          </div>

          {preview && (
            <Button variant="ghost" size="sm" onClick={reset} disabled={uploading} className="w-full text-muted-foreground">
              <RotateCcw className="w-3 h-3 mr-1" />Descartar prévia
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
